import { Color, Spacing } from '@persona-id/design-tokens';
import { VFC } from 'react';
import styled from 'styled-components';

import DraggableDivider from 'dashboard/components/WorkflowControlPanel/DraggableDivider';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import CodeEditor from 'dashboard/components-lib/CodeEditor';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { WorkflowActionSendEmailV1 } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';

const Clickable = styled.div`
  cursor: pointer;
`;

const EditorContainer = styled.div`
  height: 100%;
`;

const StyledCodeHighlight = styled(CodeHighlight)`
  border: 1px solid ${({ theme }) => theme.color.border.input};
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const StyledSticker = styled(Sticker)`
  position: absolute;
  top: -${Spacing[3]};
  left: ${Spacing[4]};
  border: 1px solid ${Color.CorePurple200};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

interface ActionOutputSendEmailV1Props {
  params: WorkflowActionSendEmailV1['params'];
}

const ActionOutputSendEmailV1: VFC<ActionOutputSendEmailV1Props> = ({ params }) => {
  const [isOpenedHoverPanel, { enable: openHoverPanel, disable: closeHoverPanel }] =
    useToggle(false);

  return (
    <>
      <ParamLabel labelText='To' />
      <Paragraph>{params['recipient']}</Paragraph>
      <Spacer height={2} />
      <ParamLabel labelText='Subject' />
      <Paragraph>{params['subject']}</Paragraph>
      <Spacer height={2} />
      <Row>
        <ParamLabel labelText='Body (HTML)' />
        <HelpIcon>Appearance may vary on different browsers and mail clients.</HelpIcon>
      </Row>
      <Clickable
        id='action-output-send-email__body-html'
        data-test='action-output-send-email__body-html'
        onClick={openHoverPanel}
      >
        <StyledCodeHighlight language='handlebars' light>
          {params['body-html'].split('\n').slice(0, 15).join('\n')}
        </StyledCodeHighlight>
      </Clickable>
      {isOpenedHoverPanel && (
        <MaximizablePanel
          title='Email Previewer'
          maximized
          onOutsideClickAndEscPress={closeHoverPanel}
        >
          {(fullScreen: boolean) => (
            <div style={{ height: fullScreen ? 'calc(100% - 48px)' : '450px' }}>
              <DraggableDivider
                orientation='horizontal'
                firstComponent={
                  <EditorContainer>
                    <CodeEditor code={params['body-html']} language='html' readOnly />
                  </EditorContainer>
                }
                secondComponent={
                  <div style={{ height: '100%', position: 'relative' }}>
                    <StyledSticker variant='primary'>Preview</StyledSticker>
                    <iframe srcDoc={params['body-html']} sandbox='' height='100%' width='100%' />
                  </div>
                }
                firstMinHeight='10px'
                secondMinHeight='10px'
                firstStyle={{ overflow: 'hidden' }}
              />
            </div>
          )}
        </MaximizablePanel>
      )}
      <Spacer height={2} />
      <ParamLabel labelText='Body (Text)' htmlFor='workflow-output-action-send-email__body-text' />
      <Paragraph>{params['body-text']}</Paragraph>
    </>
  );
};

export default ActionOutputSendEmailV1;
